import { computed, type ComputedRef, type Ref, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { useRegionPlugin } from '@/plugins/region'
import type { JewelleriesProps } from './Jewelleries.vue'
import {
    type CatalogueJewelleriesBraceletsFilterInput,
    type CatalogueJewelleriesEarringsFilterInput,
    type CatalogueJewelleriesFilterInput,
    type CatalogueJewelleriesNecklacesFilterInput,
    type CatalogueJewelleriesRingsFilterInput,
    type CatalogueSortOrder,
    type JewelleryBraceletFiltersFragment,
    JewelleryBraceletProductStyle,
    type JewelleryBrowseQuery,
    type JewelleryEarringsFiltersFragment,
    JewelleryEarringsProductStyle,
    type JewelleryFiltersFragment,
    type JewelleryNecklaceFiltersFragment,
    JewelleryNecklaceProductStyle,
    JewelleryRingProductStyle,
    type JewelleryRingsFiltersFragment,
    type MetalCode,
    type ProductGridFragment,
    type ProductPageFacetedContentInput,
} from '@/graphql/api'
import { assertNever, includes } from '@/helpers'
import { useTranslator } from '@/i18n'
import {
    type ErrorCollectionWithTraces,
    useResponseError,
} from '@/helpers/errors'
import {
    getJewelleryProductTypeLabel,
    type JewelleryProductTypeLabel,
} from './getJewelleryProductTypeLabel'
import { JewelleryProductType } from '@/router'
import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import type { InputRangeModel } from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useJewelleryBrowseQuery } from '@/views/product/jewellery/Jewelleries.generated.ts'
import type { CombinedError } from '@urql/vue'

export type JewelleryProductTypes = [
    {
        label: JewelleryProductTypeLabel<typeof JewelleryProductType.Necklace>
        totalResults: number
    },
    {
        label: JewelleryProductTypeLabel<typeof JewelleryProductType.Bracelet>
        totalResults: number
    },
    {
        label: JewelleryProductTypeLabel<typeof JewelleryProductType.Earrings>
        totalResults: number
    },
    // TODO uncomment for ring launch
    {
        label: JewelleryProductTypeLabel<typeof JewelleryProductType.Ring>
        totalResults: number
    },
]

export type JewelleryFiltersWithProductStyles =
    | {
          available: JewelleryBraceletFiltersFragment
          selected: CatalogueJewelleriesBraceletsFilterInput
      }
    | {
          available: JewelleryNecklaceFiltersFragment
          selected: CatalogueJewelleriesNecklacesFilterInput
      }
    | {
          available: JewelleryEarringsFiltersFragment
          selected: CatalogueJewelleriesEarringsFilterInput
      }
    | {
          available: JewelleryRingsFiltersFragment
          selected: CatalogueJewelleriesRingsFilterInput
      }

export type JewelleryFilters =
    | {
          available: JewelleryFiltersFragment
          selected: CatalogueJewelleriesFilterInput
      }
    | JewelleryFiltersWithProductStyles

type JewelleryData =
    | ({
          items: ProductGridFragment[]
          count: number
      } & {
          filters: JewelleryFilters
      })
    | undefined

interface JewelleryDataReturn {
    filterInput: ComputedRef<
        | CatalogueJewelleriesNecklacesFilterInput
        | CatalogueJewelleriesBraceletsFilterInput
        | CatalogueJewelleriesEarringsFilterInput
        | CatalogueJewelleriesFilterInput
    >
    activeFacetedFilterCount: ComputedRef<number>
    fetching: Ref<boolean>
    stale: Ref<boolean>
    data: Ref<JewelleryBrowseQuery | undefined>
    dataComputed: Ref<JewelleryData | undefined>
    error: Ref<CombinedError | undefined>
    reloadPage: () => void
    responseErrors: ComputedRef<ErrorCollectionWithTraces>
    perPage: number
    jewelleryProductTypes: ComputedRef<JewelleryProductTypes>
}

export async function useJewelleryData(
    props: JewelleriesProps,
    getRoutePrice: (values: InputRangeModel) => ButtonRouteRelative,
    search: Ref<string>,
    sortBy: Ref<CatalogueSortOrder>
): Promise<JewelleryDataReturn> {
    const { regionCode } = useRegionPlugin()
    const currencyInput = useCurrencyInput()

    const router = useRouter()

    const facetedContentInput = computed(() => ({
        jewellery: {
            designStyle: props.designStyle,
            metal: props.metal?.[0],
            productStyle: props.productStyle,
            gemstone: props.gemstone,
            category: props.productType,
        },
    })) satisfies ComputedRef<ProductPageFacetedContentInput>

    const activeFacetedFilterCount = computed(
        () =>
            Object.values(facetedContentInput.value.jewellery).filter(Boolean)
                .length
    )

    const filterInputBase = computed<CatalogueJewelleriesFilterInput>(() => {
        return {
            primaryMetalCodes:
                props.metal?.length && props.metal[0] === undefined
                    ? []
                    : (props.metal as MetalCode[]) || [],
            designStyles: props.designStyle ? [props.designStyle] : [],
            gemstones: props.gemstone ? [props.gemstone] : [],
            price: {
                region: regionCode.value,
                currency: currencyInput.value,
                min: props.priceMin ? String(props.priceMin) : null,
                max: props.priceMax ? String(props.priceMax) : null,
            },
            search: search.value !== '' ? search.value : null,
        }
    })

    function getProductStyles<
        T extends
            | typeof JewelleryBraceletProductStyle
            | typeof JewelleryNecklaceProductStyle
            | typeof JewelleryEarringsProductStyle
            | typeof JewelleryRingProductStyle,
    >(styles: T) {
        return includes(Object.values(styles), props.productStyle)
            ? ([props.productStyle] as [T[keyof T]])
            : null
    }

    const braceletFilters = computed<CatalogueJewelleriesBraceletsFilterInput>(
        () => ({
            ...filterInputBase.value,
            productStyles: getProductStyles(JewelleryBraceletProductStyle),
        })
    )

    const necklacesFilters = computed<CatalogueJewelleriesNecklacesFilterInput>(
        () => ({
            ...filterInputBase.value,
            productStyles: getProductStyles(JewelleryNecklaceProductStyle),
        })
    )

    const earringsFilters = computed<CatalogueJewelleriesEarringsFilterInput>(
        () => ({
            ...filterInputBase.value,
            productStyles: getProductStyles(JewelleryEarringsProductStyle),
        })
    )

    const ringsFilters = computed<CatalogueJewelleriesFilterInput>(() => ({
        ...filterInputBase.value,
        productStyles: getProductStyles(JewelleryRingProductStyle),
    }))

    const filterInput = computed(() => {
        switch (props.productType) {
            case JewelleryProductType.Necklace:
                return necklacesFilters.value
            case JewelleryProductType.Bracelet:
                return braceletFilters.value
            case JewelleryProductType.Earrings:
                return earringsFilters.value
            case JewelleryProductType.Ring:
                return ringsFilters.value
            case undefined:
                return filterInputBase.value
            default:
                return assertNever(props.productType, filterInputBase.value)
        }
    })

    const isJewellery = computed(() => !props.productType)
    const isNecklace = computed(() => props.productType === 'NECKLACE')
    const isBracelet = computed(() => props.productType === 'BRACELET')
    const isEarrings = computed(() => props.productType === 'EARRINGS')
    const isRing = computed(() => props.productType === 'RING')

    const perPage = 32
    const { locale } = useTranslator()

    const variables = computed(() => ({
        filter: filterInputBase.value,
        braceletFilter: braceletFilters.value,
        necklaceFilter: necklacesFilters.value,
        earringsFilter: earringsFilters.value,
        ringsFilter: ringsFilters.value,
        isJewellery: isJewellery.value,
        isNecklace: isNecklace.value,
        isBracelet: isBracelet.value,
        isEarrings: isEarrings.value,
        isRing: isRing.value,
        currency: currencyInput.value,
        region: regionCode.value,
        page: props.page,
        limit: perPage,
        locale: locale.value,
        path: 'jewellery',
        facetedContentInput: facetedContentInput.value,
        sortBy: sortBy.value,
    }))

    const response = useJewelleryBrowseQuery({
        variables,
    })

    const responseErrors = useResponseError(response)

    await response

    const { fetching, stale, data, error, executeQuery } = response

    const reloadPage = () => {
        executeQuery()
    }

    const dataJewellery = computed(() => {
        if (
            data.value?.catalogueJewelleries &&
            data.value.catalogueJewelleriesFilters &&
            data.value.catalogueJewelleriesAggregate
        ) {
            return {
                items: data.value.catalogueJewelleries,
                count: data.value.catalogueJewelleriesAggregate.count,
                filters: {
                    available: data.value.catalogueJewelleriesFilters,
                    selected: filterInputBase.value,
                },
            }
        }

        return undefined
    })

    const dataBracelets = computed(() => {
        if (
            data.value?.catalogueJewelleryBracelets &&
            data.value.catalogueJewelleryBraceletsFilters &&
            data.value.catalogueJewelleryBraceletsAggregate
        ) {
            return {
                items: data.value.catalogueJewelleryBracelets,
                count: data.value.catalogueJewelleryBraceletsAggregate.count,
                filters: {
                    available: data.value.catalogueJewelleryBraceletsFilters,
                    selected: braceletFilters.value,
                },
            }
        }

        return undefined
    })

    const dataNecklaces = computed(() => {
        if (
            data.value?.catalogueJewelleryNecklaces &&
            data.value.catalogueJewelleryNecklacesFilters &&
            data.value.catalogueJewelleryNecklacesAggregate
        ) {
            return {
                items: data.value.catalogueJewelleryNecklaces,
                count: data.value.catalogueJewelleryNecklacesAggregate.count,
                filters: {
                    available: data.value.catalogueJewelleryNecklacesFilters,
                    selected: necklacesFilters.value,
                },
            }
        }

        return undefined
    })

    const dataEarrings = computed(() => {
        if (
            data.value?.catalogueJewelleryEarrings &&
            data.value.catalogueJewelleryEarringsFilters &&
            data.value.catalogueJewelleryEarringsAggregate
        ) {
            return {
                items: data.value.catalogueJewelleryEarrings,
                count: data.value.catalogueJewelleryEarringsAggregate.count,
                filters: {
                    available: data.value.catalogueJewelleryEarringsFilters,
                    selected: earringsFilters.value,
                },
            }
        }

        return undefined
    })

    const dataRings = computed(() => {
        if (
            data.value?.catalogueJewelleryRings &&
            data.value.catalogueJewelleryRingsFilters &&
            data.value.catalogueJewelleryRingsAggregate
        ) {
            return {
                items: data.value.catalogueJewelleryRings,
                count: data.value.catalogueJewelleryRingsAggregate.count,
                filters: {
                    available: data.value.catalogueJewelleryRingsFilters,
                    selected: ringsFilters.value,
                },
            }
        }

        return undefined
    })

    const dataComputed = ref<JewelleryData | undefined>(undefined)

    const getData = (productType: JewelleryProductType | undefined) => {
        switch (productType) {
            case JewelleryProductType.Necklace:
                return dataNecklaces.value
            case JewelleryProductType.Bracelet:
                return dataBracelets.value
            case JewelleryProductType.Earrings:
                return dataEarrings.value
            case JewelleryProductType.Ring:
                return dataRings.value
            case undefined:
                return dataJewellery.value
            default:
                return assertNever(productType, undefined)
        }
    }

    dataComputed.value = getData(props.productType)

    watch(
        [
            () => props.productType,
            data,
            () => props.metal,
            () => props.productStyle,
            () => props.gemstone,
            () => props.designStyle,
            () => props.priceMin,
            () => props.priceMax,
        ],
        ([newProductType], [oldProducType]) => {
            dataComputed.value =
                getData(newProductType) || getData(oldProducType)
        },
        { immediate: true }
    )

    const jewelleryProductTypes = computed<JewelleryProductTypes>(() => [
        {
            label: getJewelleryProductTypeLabel(JewelleryProductType.Necklace),
            totalResults:
                data.value?.catalogueJewelleryNecklacesAggregate.count || 0,
        },
        {
            label: getJewelleryProductTypeLabel(JewelleryProductType.Bracelet),
            totalResults:
                data.value?.catalogueJewelleryBraceletsAggregate.count || 0,
        },
        {
            label: getJewelleryProductTypeLabel(JewelleryProductType.Earrings),
            totalResults:
                data.value?.catalogueJewelleryEarringsAggregate.count || 0,
        },
        {
            label: getJewelleryProductTypeLabel(JewelleryProductType.Ring),
            totalResults:
                data.value?.catalogueJewelleryRingsAggregate.count || 0,
        },
    ])

    // It seems that a cached value would be more appropriate then a toRef
    const bounds = computed(
        () => dataComputed.value?.filters.available.price.currentRange
    )

    watch(
        bounds,
        (newBounds) => {
            if (
                props.priceMin === undefined ||
                props.priceMax === undefined ||
                !newBounds
            ) {
                return
            }

            const newBoundsMin = Number(newBounds.min.amount)
            const newBoundsMax = Number(newBounds.max.amount)

            // remove if any crosses the bounds OR
            // both are the same as the new bounds
            if (
                props.priceMin < newBoundsMin ||
                props.priceMax > newBoundsMax ||
                (props.priceMin === newBoundsMin &&
                    props.priceMax === newBoundsMax)
            ) {
                router.replace(getRoutePrice([undefined, undefined]))
            }
        },
        { immediate: true }
    )

    return {
        filterInput,
        activeFacetedFilterCount,
        fetching,
        stale,
        data,
        dataComputed,
        error,
        reloadPage,
        responseErrors,
        perPage,
        jewelleryProductTypes,
    }
}
